import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Chip,
  Alert,
  alpha,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { adminGetUserPassbook } from "../api";

const LIMIT = 20;

const formatINR = (v) =>
  v == null || v === "" || isNaN(Number(v))
    ? "—"
    : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const GATEWAY_LABELS = {
  general: "General",
  paytm: "Paytm",
  cashfree: "Cashfree",
  ferry: "Pure Travel",
  slpe: "SLPE",
  superpe: "SuperPe",
};
const gatewayLabel = (g) =>
  GATEWAY_LABELS[String(g || "").toLowerCase()] || g || "Commission";

const MAIN_KEY = "main";

export default function AdminUserPassbook() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userName = location.state?.name || "";

  const [summary, setSummary] = useState(null);
  const [walletKey, setWalletKey] = useState(MAIN_KEY);
  const [order, setOrder] = useState("desc");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* Wallet options from the summary (Main + each commission wallet). */
  const commissionWallets = summary?.commissionWallets || [];
  const wallets = [
    {
      key: MAIN_KEY,
      label: "Main Wallet",
      walletType: "main",
      balance: summary?.mainBalance ?? 0,
      icon: <AccountBalanceWalletRoundedIcon />,
      color: "#1c6b8c",
    },
    ...commissionWallets.map((w) => ({
      key: `commission:${w.type || ""}:${w.gateway || ""}`,
      label: `${gatewayLabel(w.gateway)} Commission`,
      walletType: w.type || "commission",
      gateway: w.gateway,
      balance: w.balance ?? 0,
      icon: <SavingsRoundedIcon />,
      color: "#f59e0b",
    })),
  ];
  const activeWallet = wallets.find((w) => w.key === walletKey) || wallets[0];

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    const params = {
      walletType: activeWallet?.walletType,
      gateway: activeWallet?.gateway || undefined,
      page,
      limit: LIMIT,
      order,
    };
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = `${toDate}T23:59:59`;
    adminGetUserPassbook(id, params)
      .then(({ data }) => {
        setSummary(data.summary || null);
        setRows(data.transactions || []);
        setTotal(data.total ?? data.pagination?.total ?? 0);
      })
      .catch((e) => {
        setError(e.response?.data?.error || "Failed to load passbook");
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeWallet?.walletType, activeWallet?.gateway, page, order, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const pageCount = Math.ceil(total / LIMIT);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton size="small" onClick={() => navigate(`/admin/users/${id}`)}>
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              User Passbook
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userName
                ? `Every wallet entry for ${userName}`
                : "Every wallet entry for this user — verify credits/debits"}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="passbook-wallet">Wallet</InputLabel>
            <Select
              labelId="passbook-wallet"
              label="Wallet"
              value={activeWallet?.key || MAIN_KEY}
              onChange={(e) => {
                setWalletKey(e.target.value);
                setPage(1);
              }}
            >
              {wallets.map((w) => (
                <MenuItem key={w.key} value={w.key}>
                  {w.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton size="small" onClick={load} title="Reload">
            <RefreshRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ p: 2, mb: 3, maxWidth: 340 }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Avatar
            sx={{
              bgcolor: alpha(activeWallet?.color || "#1c6b8c", 0.14),
              color: activeWallet?.color || "#1c6b8c",
              width: 40,
              height: 40,
            }}
          >
            {activeWallet?.icon}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap>
              {activeWallet?.label} balance
            </Typography>
            <Typography variant="h6" fontWeight={700} noWrap>
              {formatINR(activeWallet?.balance)}
            </Typography>
          </Box>
        </Stack>
      </Card>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1.5}
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            size="small"
            type="date"
            label="From"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: fromDate || undefined }}
            sx={{ width: 160 }}
          />
          {(fromDate || toDate) && (
            <Button
              size="small"
              onClick={() => {
                setFromDate("");
                setToDate("");
                setPage(1);
              }}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Clear
            </Button>
          )}
        </Stack>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={order}
          onChange={(_, v) => {
            if (!v) return;
            setOrder(v);
            setPage(1);
          }}
          sx={{
            "& .MuiToggleButton-root": {
              px: 2,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            },
          }}
        >
          <ToggleButton value="desc">Newest first</ToggleButton>
          <ToggleButton value="asc">Oldest first</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Card sx={{ overflow: "hidden", borderRadius: 3 }}>
        <Box sx={{ overflowX: "auto" }}>
          {loading ? (
            <Box sx={{ p: 3 }}>
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} height={44} sx={{ mb: 0.5 }} />
              ))}
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <ReceiptLongRoundedIcon
                sx={{ fontSize: 52, color: "text.disabled", mb: 1 }}
              />
              <Typography fontWeight={600} mb={0.5}>
                No entries
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No credits or debits for this wallet in the selected range
              </Typography>
            </Box>
          ) : (
            <Table sx={{ minWidth: 1140 }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      color: "text.secondary",
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Card Details</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((t) => {
                  const type = String(t.type || t.txnType || "").toLowerCase();
                  const isCredit = type === "credit";
                  const amount = t.amount ?? 0;
                  const balanceAfter = t.balance_after ?? t.balanceAfter;
                  return (
                    <TableRow key={t.id} hover>
                      <TableCell
                        sx={{ color: "text.secondary", fontSize: 13, whiteSpace: "nowrap" }}
                      >
                        {formatDate(t.created_at || t.createdAt)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {t.description || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {t.reference_type || t.referenceType ? (
                          <Chip
                            size="small"
                            label={t.reference_type || t.referenceType}
                            sx={{ fontSize: 10, height: 20, textTransform: "capitalize" }}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {t.payment_category ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t.payment_category}
                            sx={{ fontSize: 10, height: 20, textTransform: "capitalize" }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {t.card_info?.last4 ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {t.card_info.bank || "Card"} •••• {t.card_info.last4}
                            </Typography>
                            {(t.card_info.network || t.card_info.cardType) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: "capitalize" }}
                              >
                                {[t.card_info.network, t.card_info.cardType]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {t.customer?.name || t.customer?.mobile || t.customer?.email ? (
                          <Box>
                            {t.customer.name && (
                              <Typography variant="body2" fontWeight={600}>
                                {t.customer.name}
                              </Typography>
                            )}
                            {(t.customer.mobile || t.customer.email) && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {[t.customer.mobile, t.customer.email]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, whiteSpace: "nowrap", color: "error.main" }}
                      >
                        {!isCredit ? `- ${formatINR(amount)}` : ""}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, whiteSpace: "nowrap", color: "success.main" }}
                      >
                        {isCredit ? `+ ${formatINR(amount)}` : ""}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                      >
                        {balanceAfter != null ? formatINR(balanceAfter) : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Box>
        {pageCount > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>
    </Box>
  );
}
